"use client";

import Navbar from "../../components/Layout/Navbar";
import Footer from "../../components/Layout/Footer";
import PageMeta from "../../components/pagesHelmet/PageMeta";

const BRAND = {
  primary: "#3B82F6",
  primaryDark: "#2563EB",
  primarySoft: "rgba(59, 130, 246, 0.1)",
  success: "#22C55E",
  warning: "#F59E0B",
  muted: "#6B7280",
  text: "#1F2937",
};

export default function TermsOfServicePage() {
  return (
    <>
    <PageMeta 
      title="Terms Of Service | U2LR URL Shortener"  
      description="Review the Terms of Service for U2LR, covering usage rules, subscription plans, and the legal agreement for using our URL shortener, QR codes, and analytics tools." 
    />
      <Navbar />

      <div className="bg-white">

        {/* ================= HERO ================= */}
        <section
          className="text-white"
          style={{
            background: `linear-gradient(135deg, #000, #4b76d1)`,
          }}
        >
          <div className="container py-5">
            <div className="row justify-content-center text-center">
              <div className="col-lg-8 py-5">
                <h1 className="display-6 fw-bold mb-3">Terms of Service</h1>
                <p className="opacity-75 mb-0">
                  Please read these terms carefully before using u2lr.
                </p>
                <small className="d-block mt-3 opacity-75">
                  Last Updated: January 5, 2026
                </small>
              </div>
            </div>
          </div>
        </section>

        {/* ================= CONTENT ================= */}
        <section className="py-5">
          <div className="container py-4">
            <div className="row justify-content-center">
              <div className="col-lg-9">

                <TermsSection title="1. Introduction & Scope">
                  <p>
                    Welcome to u2lr.com, a URL shortening and link management
                    platform operated by <strong>TegaFate HuB</strong>.
                  </p>
                  <p>
                    These Terms govern your access to and use of u2lr.com and all
                    related services, tools, dashboards, and APIs.
                  </p>
                </TermsSection>

                <TermsSection title="2. Acceptance of Terms">
                  <p>
                    By creating an account or using u2lr.com, you confirm that
                    you have read, understood, and agreed to these Terms and our
                    Privacy Policy. This forms a legally binding agreement
                    between you and TegaFate HuB.
                  </p>
                </TermsSection>

                <TermsSection title="3. Eligibility">
                  <ul>
                    <li>
                      <strong>Age:</strong> You must be at least 13 years old (or
                      the minimum legal age in your jurisdiction).
                    </li>
                    <li>
                      <strong>Authority:</strong> If using u2lr on behalf of an
                      organization, you confirm you have authority to bind that
                      entity.
                    </li>
                  </ul>
                </TermsSection>

                <TermsSection title="4. Account Registration & Security">
                  <ul>
                    <li>Provide accurate and complete registration information</li>
                    <li>Maintain the confidentiality of your credentials</li>
                    <li>Notify us immediately of unauthorized access</li>
                  </ul>
                </TermsSection>

                <TermsSection title="5. Service Description">
                  <ul>
                    <li>URL shortening</li>
                    <li>Link management and organization</li>
                    <li>Click and performance analytics</li>
                  </ul>
                  <p>
                    We reserve the right to modify, suspend, or discontinue any
                    part of the service at any time.
                  </p>
                </TermsSection>

                <TermsSection title="6. User Responsibilities & Prohibited Activities">
                  <p>You are responsible for the links you create.</p>
                  <ul>
                    <li>Illegal content, malware, or phishing</li>
                    <li>Spam or unsolicited communications</li>
                    <li>Abuse of infrastructure or security circumvention</li>
                    <li>Fraud, deception, or identity theft</li>
                  </ul>
                </TermsSection>

                <TermsSection title="7. Content Ownership & License">
                  <p>
                    You retain ownership of the URLs and destination content you
                    process through u2lr.
                  </p>
                  <p>
                    You grant TegaFate HuB a limited license to host and redirect
                    your links solely to provide the service.
                  </p>
                </TermsSection>

                <TermsSection title="8. Fees & Payment">
                  <ul>
                    <li>Some features may require payment</li>
                    <li>Fees are disclosed at purchase</li>
                    <li>Subscriptions are billed in advance</li>
                    <li>You are responsible for applicable taxes</li>
                  </ul>
                </TermsSection>

                <TermsSection title="9. Refund Policy">
                  <p>
                    Payments are generally non-refundable unless required by
                    local consumer law. Refunds may be issued at our discretion
                    for billing errors or service failures.
                  </p>
                </TermsSection>

                <TermsSection title="10. Privacy & Data Handling">
                  <p>
                    Our data practices, including GDPR and CCPA compliance, are
                    described in our Privacy Policy.
                  </p>
                </TermsSection>

                <TermsSection title="11. Intellectual Property">
                  <p>
                    All software, branding, and design related to u2lr are the
                    exclusive property of TegaFate HuB. Unauthorized use is
                    prohibited.
                  </p>
                </TermsSection>

                <TermsSection title="12. Disclaimers & Limitation of Liability">
                  <p>
                    The service is provided on an “as-is” basis without
                    warranties of any kind.
                  </p>
                  <p>
                    To the maximum extent permitted by law, TegaFate HuB is not
                    liable for indirect or consequential damages.
                  </p>
                </TermsSection>

                <TermsSection title="13. Indemnification">
                  <p>
                    You agree to indemnify and hold TegaFate HuB harmless from
                    claims arising from your misuse of the service or violation
                    of these Terms.
                  </p>
                </TermsSection>

                <TermsSection title="14. Termination & Suspension">
                  <p>
                    We may suspend or terminate accounts for violations,
                    fraudulent activity, or non-payment. Access to the service
                    will cease immediately upon termination.
                  </p>
                </TermsSection>

                <TermsSection title="15. Dispute Resolution & Governing Law">
                  <p>
                    These Terms are governed by the laws of the jurisdiction
                    where TegaFate HuB is registered. Disputes will be resolved
                    through binding arbitration after informal negotiation.
                  </p>
                </TermsSection>

                <TermsSection title="16. Changes to the Terms">
                  <p>
                    We may update these Terms from time to time. Continued use of
                    u2lr constitutes acceptance of the revised Terms.
                  </p>
                </TermsSection>

                <TermsSection title="17. Contact Information">
                  <p className="mb-1">
                    <strong>Email:</strong> support@u2lr.com
                  </p>
                  <p className="mb-0">
                    <strong>Website:</strong> u2lr.com/contact
                  </p>
                </TermsSection>

              </div>
            </div>
          </div>
        </section>

        {/* ================= FOOT NOTE ================= */}
        <section
          className="text-center py-5"
          style={{ backgroundColor: BRAND.primarySoft }}
        >
          <div className="container">
            <p className="mb-0 text-muted">
              These Terms exist to protect both you and the u2lr platform.
            </p>
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
}

/* ================= HELPER ================= */

function TermsSection({ title, children }) {
  return (
    <div className="mb-5">
      <h4 className="fw-bold mb-3">{title}</h4>
      <div className="text-muted fs-6">{children}</div>
    </div>
  );
}
